/**
****************************************************************************************
Project: Readabl
File: PDFViewer.js
Description: This reusable component will get pdf url as input props.
Converts pdf pages into images, handles component level states and events.
© Healthcare Triangle, Inc
****************************************************************************************
**/

import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import scrollIntoView from 'scroll-into-view-if-needed'
import "./PDFViewer.css";
import { FileDownload, SelectAll, Close, Rotate90DegreesCw } from "@mui/icons-material";
import { saveAs } from 'file-saver';
import {
    RenderImage,
    PdfRenderLoader,
    CircularProgressWithLabel,
    Header,
    RotateLayer,
    Thumbnails,
} from "./Components";
import * as pdfjsLib from "pdfjs-dist";
import printJS from "print-js";
import { usePdfScrollTracker } from "./customHooks/usePdfScrollTracker";
import { usePdfToImage } from "./customHooks/usePdfToImages";

// pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
//   "pdfjs-dist/build/pdf.worker.min.mjs",
//   import.meta.url
// ).toString();
// pdfjsLib.GlobalWorkerOptions.workerSrc =
//   `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const PDFViewer = forwardRef((props, ref) => {

    const previewContainerRef = useRef(null);
    const thumbNailRef = useRef(null);
    const [images, setImages] = useState([]);
    const [imageDimensions, updateImageDimensions] = useState({});
    const [zoomLevel, updateZoom] = useState(1);
    const [enableSelection, setSelectionLayers] = useState(false);
    const [selectedPages, updateSelectedPages] = useState([]);
    const [pdfLoaded, updatePDFDownloaded] = useState(0);
    const [pdfProcessed, updatePDFProcessed] = useState(0);
    const [highlightCoordinates, updateHighlightCoordinates] = useState([]);
    const [rotatedPages, updateRotatedPages] = useState({});
    const [disableRotate, setDisableRotate] = useState(false);
    const [openThumbnails, setThumbnails] = useState(false);
    const [pdfContainerDimensions, setPdfContainerDimensions] = useState({ width: Math.floor(((window.innerWidth - 40) / 100) * 50), height: Math.floor(window.innerHeight - 192) });


    const [currentPageNumber, scrollToPage] = usePdfScrollTracker(previewContainerRef, images, zoomLevel, rotatedPages);
    const {
        imageList,
        isLoading,
        downloadProgress,
        renderProgress,
        convertPdfToImage,
        cancelCurrentTask,
    } = usePdfToImage({
        batchSize: 5,
        delayBetweenBatches: 100,
        onRenderProgress: (done, total, percent) => console.log(`Rendered ${percent}%`),
        onAfterLoad: (images) => console.log("All pages rendered:", images.length),
    });

    useEffect(() => {
        setImages(imageList);
    }, [imageList]);

    useEffect(() => {
        updatePDFDownloaded(downloadProgress);
    }, [downloadProgress]);

    useEffect(() => {
        updatePDFProcessed(renderProgress);
    }, [renderProgress]);

    useEffect(() => {
        // update container width and height on resize event
        window.addEventListener('resize', () => {
            let dim = { width: Math.floor(((window.innerWidth - 40) / 100) * 50), height: Math.floor(window.innerHeight - 192) }
            setPdfContainerDimensions(dim);
            updateZoom(window.devicePixelRatio)
            updateImageAspectRatio(dim, thumbNailRef.current, true);
        });


        updateImageAspectRatio(pdfContainerDimensions);

    }, []);

    useEffect(() => {
        displaySearchText(props.searchResults, 0)
    }, [props.searchResults])

    useEffect(() => {
        if (highlightCoordinates?.length != 0) {
            let count = 0;
            // this function will scroll pages automatically to searched highlighted text 
            let timer = setInterval(() => {
                let childNode = document.getElementById("pdf-current-highlight")
                if (childNode) {
                    scrollIntoView(childNode, {
                        block: "center",
                        behavior: "smooth",
                        scrollMode: "always",
                        boundary: document.getElementById("pdf-viewer-main-container-wrapper"),
                    });
                    clearInterval(timer)
                } else if (count == 20) clearInterval(timer);
                else count++;

            }, 500)
        }
    }, [highlightCoordinates]);

    useEffect(() => {
        if (props.highlightData?.length) {
            displaySearchText(props.highlightData, 0)
        }
    }, [props.highlightData]);

    useEffect(() => {
        thumbNailRef.current = openThumbnails
    }, [openThumbnails])

    useEffect(() => {
        typeof props.handleSelection == "function" && props.handleSelection(selectedPages, selectionCallBack);
    }, [selectedPages]);

    useEffect(() => {
        setImages([]);
        scrollToPage(1);
        updateSelectedPages([]);
        setSelectionLayers(false);
        updatePDFDownloaded(0);
        updateHighlightCoordinates([]);
        updateRotatedPages(props.rotatedPages || {});
        setTimeout(() => {
            convertPdfToImage(props.pdfUrl);
        }, 0);
    }, [props.pdfUrl]);

    useImperativeHandle(ref, () => ({
        rotatedPages, selectedPages,
        setSelectionLayers, updateHighlightCoordinates, updateRotatedPages, updateSelectedPages, setDisableRotate
    }));

    const updateImageAspectRatio = (dim, isAdjust, isResize) => {
        let width = 927, height = 1200;
        let newWidth = width, newHeight = height;
        if (isResize) {
            width = width * window.devicePixelRatio;
            height = height * window.devicePixelRatio;
        }
        if (isAdjust) {
            newWidth -= 200;
            newHeight = Math.round(newWidth * height / width);
        }
        updateImageDimensions({ width: newWidth, height: newHeight });

    }

    const selectionCallBack = () => {
        enableSelectionLayers();
    }
    // this will enable the selection layers
    const enableSelectionLayers = () => {
        setSelectionLayers(prevState => !prevState);
        updateSelectedPages([]);

    }

    // this will handle selection and de-selection of pages
    const handleSelection = (action, pageNumber) => {
        let filteredPages = selectedPages.filter(m => m != pageNumber);
        if (action == "select") {
            filteredPages.push(pageNumber);
            updateSelectedPages(prevState => {
                return filteredPages
            });
        } else if (action == "deselect") {
            updateSelectedPages(prevState => {
                return filteredPages
            });
        }

        typeof props.onSelectionChange == "function" && props.onSelectionChange(filteredPages);

    }

    /*
    * adding colours to highlighted text
    * red color is for currently viewing text
    * orange color is for all searched text
    * */
    const addSelectedColor = (arr, index) => {
        let newArray = JSON.parse(JSON.stringify(arr || []));
        return (newArray || []).map((m, i) => {
            if (i == index) {
                m.color = "red";
                m.selectedId = "pdf-current-highlight";
            } else {
                m.color = "orange";
                delete m.selectedId;
            }
            return m;
        });
    }

    const displaySearchText = (searchResults, currentIndex) => {
        let final = addSelectedColor(searchResults, currentIndex);
        updateHighlightCoordinates(final);
    }


    const handleRotate = () => {
        let temp = JSON.parse(JSON.stringify(rotatedPages || {}));
        // if (Object.keys(temp)?.length || selectedPages?.length) {
        if (selectedPages?.length) {
            for (let index = 0; index < selectedPages.length; index++) {
                const pageNumber = selectedPages[index];
                temp[pageNumber] = getRotatedAngle(temp[pageNumber]);
            }
            // } else temp[currentPageNumber] = getRotatedAngle(temp[currentPageNumber])
        } else {
            images.forEach((m, i) => {
                temp[i + 1] = getRotatedAngle(temp[i + 1]);
            })
        }
        updateRotatedPages(temp);
        typeof props.onRotationChange == "function" && props.onRotationChange(temp);
    }

    const getRotatedAngle = (prev) => {
        if (prev == 90) {
            return 360;
        } else {
            return (prev || 360) - 90
        }
    }


    const handlePrint = async () => {
        printJS({
            printable: props.pdfUrl,
            type: "pdf",
            showModal: true,
            modalMessage: "Preparing document for printing...",
            documentTitle: props.fileName || "Document",
            onError: (error) => {
                console.error("PDF printing failed:", error);
            },
        });
    };

    const handleThumbnailMenuClick = () => {
        updateImageAspectRatio(pdfContainerDimensions, !openThumbnails);
        setThumbnails(prevState => !prevState);
    }

    const handleThumbnailClick = (page) => {
        scrollToPage(page);
    }

    return (
        <div className="pdf-viewer-wrapper-container"
            style={{ width: pdfContainerDimensions.width, height: "fit-content" }}>
            <Header
                images={images}
                pdfUrl={props.pdfUrl}
                totalPages={props.totalPages}
                currentPageNumber={currentPageNumber}
                zoomLevel={zoomLevel}
                updateZoom={updateZoom}
                enableSelection={enableSelection}
                disableRotate={disableRotate}
                enableSelectionLayers={enableSelectionLayers}
                selectedPages={selectedPages}
                fileName={props.fileName}
                displaySearchText={displaySearchText}
                handleSearch={props.handleSearch}
                searchResults={props.searchResults}
                onSearchClose={props.onSearchClose}
                searchString={props.searchString}
                showSearch={props.showSearch}
                showSelection={props.showSelection}
                handleRotate={handleRotate}
                handlePrint={handlePrint}
                handleThumbnailMenuClick={handleThumbnailMenuClick}
            />
            <div style={{ display: "flex" }}>
                {openThumbnails && images?.length &&
                    <Thumbnails
                        images={images}
                        config={{
                            containerHeight: pdfContainerDimensions.height
                        }}
                        currentPageNumber={currentPageNumber}
                        rotatedPages={rotatedPages}
                        handleThumbnailClick={handleThumbnailClick}
                    /> || null}
                <div id="pdf-viewer-main-container-wrapper" className="pdf-viewer-main-container-wrapper" ref={previewContainerRef} style={{ width: "-webkit-fill-available", overflow: "auto" }}>
                    <PdfRenderLoader percentage={pdfProcessed} />
                    <div
                        className="pdf-viewer-main-container"
                        style={{
                            width: imageDimensions.width,
                            height: pdfContainerDimensions.height,
                            margin: "auto",
                            transformOrigin: zoomLevel < 1 ? "center top" : "left top"
                        }}>

                        {images?.length ?
                            <>
                                <div
                                    className="pdf-viewer-image-container"
                                    style={{
                                        transform: `scale(${zoomLevel})`,
                                        transformOrigin: zoomLevel < 1 ? "center top" : "left top"
                                    }}>
                                    <RenderImage
                                        images={images}
                                        imageDimensions={imageDimensions}
                                        highlightCoordinates={highlightCoordinates}
                                        disabledPages={props.disabledPages}
                                        selectedPages={selectedPages}
                                        handleSelection={handleSelection}
                                        selectionEnabled={enableSelection}
                                        disabledText={props.disabledText || "Disabled"}
                                        rotatedPages={rotatedPages}
                                        zoomLevel={zoomLevel}
                                    />
                                </div>
                            </>
                            : (pdfLoaded > 0 ? <CircularProgressWithLabel variant="determinate" size={50} value={pdfLoaded} style={{ color: "#fff", margin: "auto", zIndex: 1 }} /> : <div className="pdf-viewer-circular-loader-container">
                                <CircularProgress size={30} style={{ color: "#fff", margin: "auto", zIndex: 1, position: "absolute" }} />
                            </div>)}
                    </div>
                </div>
            </div>

        </div>
    )
});

export default PDFViewer;